import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import filesToStudies from '../routes/Local/filesToStudies';
import helicalLogo from '../../assets/logo.png';

const DICOM_CACHE = 'helical-cloud-dicom';

function cacheKeyFor(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.delete('vt');
    // Drop the presigned signature params so the same object caches to a stable key.
    for (const p of Array.from(u.searchParams.keys())) {
      if (p.toLowerCase().startsWith('x-amz-')) u.searchParams.delete(p);
    }
    return u.toString();
  } catch {
    return url;
  }
}

async function cachedFetch(url: string): Promise<Response> {
  if (typeof caches === 'undefined') return fetch(url);
  const key = cacheKeyFor(url);
  const cache = await caches.open(DICOM_CACHE);
  const hit = await cache.match(key);
  if (hit) return hit;

  const res = await fetch(url);
  if (res.ok) {
    try {
      await cache.put(key, res.clone());
    } catch {
      // storage full - continue without caching
    }
  }
  return res;
}

async function fetchInBatches<T, R>(
  items: T[],
  maxConcurrent: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      results[i] = await fn(items[i]);
    }
  }

  const workers = Array.from({ length: Math.min(maxConcurrent, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

const HelicalCloud: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [statusText, setStatusText] = useState('Loading study...');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    document.body.style.backgroundColor = 'hsl(var(--background))';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    const share = searchParams.get('share');
    const vt = searchParams.get('vt');
    const api = searchParams.get('api');
    const study = searchParams.get('study'); // optional: scope to one study
    const mode = searchParams.get('mode') || 'helicalcloud';

    if (!share || !vt || !api) {
      setError('SESSION_EXPIRED');
      return;
    }

    const apiBase = api.replace(/\/+$/, '');
    const auth = `share=${encodeURIComponent(share)}&vt=${encodeURIComponent(vt)}`;

    const load = async () => {
      try {
        // Step 1: fetch the whole manifest (studies, metadata, all instance URLs) in one call
        setStatusText('Fetching study list...');
        const manifestRes = await fetch(`${apiBase}/viewer/manifest?${auth}`);
        if (manifestRes.status === 401 || manifestRes.status === 403) {
          throw new Error('SESSION_EXPIRED');
        }
        if (!manifestRes.ok) {
          throw new Error(`Study list failed: ${manifestRes.status} ${manifestRes.statusText}`);
        }
        const manifestJson = await manifestRes.json();
        const apiStudies: any[] = manifestJson?.data?.studies || [];
        if (apiStudies.length === 0) {
          throw new Error('No studies found for this share.');
        }

        // Step 2: build the study list and panel metadata from the manifest
        type StudyManifest = {
          StudyInstanceUID: string;
          series: { SeriesInstanceUID: string; Images: string[] }[];
        };
        const manifestStudies: StudyManifest[] = apiStudies.map(st => ({
          StudyInstanceUID: st.study_instance_uid,
          series: (st.series || []).map((se: any) => ({
            SeriesInstanceUID: se.series_instance_uid,
            Images: se.instances || [],
          })),
        }));

        // DICOM person names keep their "Last^First" caret; swap it for a space.
        const studyMetadata: Record<string, any> = {};
        for (const st of apiStudies) {
          studyMetadata[st.study_instance_uid] = {
            PatientName: String(st.patient_name || '').replace(/\^/g, ' ').trim(),
            StudyDate: st.date || '',
            StudyDescription: st.description || '',
            Modality: (st.modalities || [])[0] || '',
          };
        }

        // Step 3: keep the full manifest on window so the panel can load other studies on demand
        (window as any).__remoteLocalManifest = {
          manifest: { studies: manifestStudies },
          studyMetadata,
          folderId: null,
          patientId: null,
        };
        // Expose session credentials for the New Tab button
        (window as any).__helicalCloudSession = { share, vt, api: apiBase, mode };

        // Step 4: open the requested study, else the smallest one so the viewer opens fastest
        const countImages = (s: StudyManifest) =>
          s.series.reduce((n, ser) => n + ser.Images.length, 0);
        let targetStudy: StudyManifest;
        if (study) {
          targetStudy = manifestStudies.find(s => s.StudyInstanceUID === study) || manifestStudies[0];
        } else {
          targetStudy = manifestStudies.reduce((smallest, current) =>
            countImages(current) < countImages(smallest) ? current : smallest
          );
        }

        const urls: string[] = [];
        for (const ser of targetStudy.series) {
          urls.push(...ser.Images);
        }
        if (urls.length === 0) {
          throw new Error('No images found for this share.');
        }

        // Step 5: download the target study's DICOMs
        const total = urls.length;
        let done = 0;
        setStatusText(`Downloading ${total} images...`);

        const files = await fetchInBatches(urls, 6, async (url: string) => {
          const res = await cachedFetch(url);
          if (!res.ok) {
            console.warn(`Failed to fetch image: ${url}`);
            return null;
          }
          const arrayBuffer = await res.arrayBuffer();
          done++;
          setProgress(Math.round((done / total) * 90));
          setStatusText(`Downloaded ${done} of ${total} images...`);

          // Proxy URLs carry the sop in `key`; presigned S3 URLs use the path.
          let fileName = 'image.dcm';
          try {
            const u = new URL(url);
            const key = u.searchParams.get('key');
            if (key) {
              fileName = `${key}.dcm`;
            } else {
              const seg = u.pathname.split('/').filter(Boolean).pop();
              if (seg) fileName = decodeURIComponent(seg);
            }
          } catch {
            /* use default */
          }
          return new File([arrayBuffer], fileName, { type: 'application/dicom' });
        });

        const validFiles = files.filter((f): f is File => f !== null);
        if (validFiles.length === 0) {
          throw new Error('No images could be downloaded.');
        }

        // Step 6: parse the files into the in-memory store
        setProgress(92);
        setStatusText(`Processing ${validFiles.length} images...`);
        const studyUIDs = await filesToStudies(validFiles);
        setProgress(100);

        if (!studyUIDs || studyUIDs.length === 0) {
          throw new Error('No valid DICOM studies found in downloaded files.');
        }

        // Step 7: remember the session so a reload can return here
        sessionStorage.setItem('helicalcloud_share', share);
        sessionStorage.setItem('helicalcloud_vt', vt);
        sessionStorage.setItem('helicalcloud_api', apiBase);
        sessionStorage.setItem('helicalcloud_mode', mode);
        sessionStorage.setItem('helicalcloud_study', study || '');

        // Step 8: hand off to the local viewer
        const query = new URLSearchParams();
        studyUIDs.forEach(id => query.append('StudyInstanceUIDs', id));
        query.append('datasources', 'dicomlocal');

        navigate(`/${mode}/dicomlocal?${decodeURIComponent(query.toString())}`, {
          replace: true,
        });
      } catch (err) {
        console.error('HelicalCloud error:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      }
    };

    load();
  }, [searchParams, navigate]);

  if (error) {
    const isExpired = error === 'SESSION_EXPIRED';
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-center max-w-sm px-4">
          <h2 className="mb-2 text-xl font-semibold text-red-400">
            {isExpired ? 'Session Expired' : 'Error'}
          </h2>
          <p className="text-gray-300">
            {isExpired
              ? 'Your viewing session has expired. Please re-open the study using the original share link.'
              : error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'hsl(var(--background))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={helicalLogo}
        alt="Helical"
        style={{
          height: '80px',
          marginBottom: '40px',
          filter: 'brightness(0) invert(1)',
          opacity: 0.95,
        }}
      />
      <div
        style={{
          width: '280px',
          height: '6px',
          backgroundColor: 'hsl(var(--card))',
          borderRadius: '3px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))',
            borderRadius: '3px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <div
        style={{
          marginTop: '12px',
          fontSize: '14px',
          fontWeight: 600,
          color: 'hsl(var(--foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {progress}%
      </div>
      <div
        style={{
          marginTop: '6px',
          fontSize: '12px',
          color: 'hsl(var(--muted-foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {statusText}
      </div>
    </div>
  );
};

export default HelicalCloud;
